/*
============================ HOLD FOR BUYER (buyer and seller views) ============================

An admin freeze that exists ONLY to change what the buyer is told about an order. The API writes
`holdForBuyer` on the order plus one entry onto `order_status` (medimny-api controllers/order.js
holdForBuyer); admin and seller carry on seeing the order exactly as it was, because `requested` is
never touched by a hold. This file serves both sides of that in this app - the buyer's whole view of
the feature, and the one cosmetic adjustment the seller's screens need (`sellerLastLogStatus`).

THE TWO LOG ENTRIES

  New and OnHold   the hold going on. Shown to the buyer as "Processed", described as "Order has
                   been processed", and while it is the LATEST entry the order's badge reads
                   Processed too.
  New and UnHold   the hold coming off. Never shown to the buyer at all. The order is a plain New
                   one again, so that is what its badge reads (which the order list then renders as
                   its usual "Placed").

WHY THE FLAG *AND* THE LAST ENTRY

`isHeldForBuyer` requires both. The flag alone is not enough: it is order-level state, while the
badge is about what most recently happened to the order. An admin who holds an order and then
processes it has `holdForBuyer` still true with a 'Processed' entry sitting after the hold - the
buyer must see that real Processed, not a frozen one. Requiring the hold to still be the last thing
of consequence keeps the facade to exactly the window it was meant for.

A hold also has a hard consequence the buyer feels rather than sees: the API refuses a buyer or
seller cancellation while it is on. The Cancel button is hidden here to match, and the Invoice
button is shown disabled - the order LOOKS processed, so an invoice control that silently does
nothing (there is no invoice yet) would be worse than one visibly greyed out.

Keep the strings in step with medimny-api (controllers/order.js) and medimny-admin
(constants/holdForBuyer.js).
*/
export const HOLD_FOR_BUYER_STATUS = 'New and OnHold';
export const UNHOLD_FOR_BUYER_STATUS = 'New and UnHold';

/*
THE RUN-ON FORMS

The two entries were originally stored without the spaces, and every hold written before the rename
still carries the run-on spelling. scripts/renameHoldForBuyerLogStatus.js in medimny-api rewrites
them, but nothing here may assume it has run: an archived order, a stale replica or an un-migrated
environment can still hand us the old value. So every comparison goes through `isHoldStatus` /
`isUnholdStatus`, and anything that renders a raw log entry goes through `logStatusLabel`, which
upgrades an old value to the spaced spelling on the way to the screen.

Once the migration has run everywhere and no order carries the run-on form, these three can go.
*/
export const LEGACY_HOLD_FOR_BUYER_STATUS = 'NewandOnHold';
export const LEGACY_UNHOLD_FOR_BUYER_STATUS = 'NewandUnHold';

export const isHoldStatus = (status) =>
  status === HOLD_FOR_BUYER_STATUS || status === LEGACY_HOLD_FOR_BUYER_STATUS;

export const isUnholdStatus = (status) =>
  status === UNHOLD_FOR_BUYER_STATUS || status === LEGACY_UNHOLD_FOR_BUYER_STATUS;

// One log status as it should be READ - the run-on forms upgraded, everything else passed through.
export const logStatusLabel = (status) => (isHoldStatus(status)
  ? HOLD_FOR_BUYER_STATUS
  : isUnholdStatus(status) ? UNHOLD_FOR_BUYER_STATUS : status);

// What the buyer is told a held order is, in place of the hold entry's real status/description.
export const HELD_STATUS_LABEL = 'Processed';
export const HELD_DESCRIPTION = 'Order has been processed';

/*
The log statuses that leave a live hold intact - the ones that can sit AFTER the hold entry without
the order having actually moved on.

Only a change request puts anything there. Accepting one (medimny-api controllers/order.js, status
'accept') pushes a plain 'New' entry, "Change order request has been Accepted.", and sets
`requested` to 'Accepted' - the revised order is the same frozen New order it was a moment ago, so
'Accepted' is carried here too for any caller reading that value as the order's last status. Without
this the buyer would watch a "Processed" order turn back into a Placed one by accepting a change,
which is exactly what the hold exists to prevent.

Anything else after the hold - Processed, Ready For Dispatch, Cancelled - is the order genuinely
moving, and the buyer must see it.
*/
const holdSurvives = (status) => isHoldStatus(status) || status === 'New' || status === 'Accepted';

export const lastLogStatus = (orderData) => {
  const logs = (orderData && orderData.order_status) || [];
  return logs.length > 0 ? logs[logs.length - 1].status : '';
};

// True only while the freeze is BOTH on and still the latest thing of consequence - see above.
export const isHeldForBuyer = (orderData) =>
  !!(orderData && orderData.holdForBuyer) && holdSurvives(lastLogStatus(orderData));

/*
The order-level status the buyer is shown, before the list's own 'New' -> 'Placed' relabelling.

Once a live hold has been ruled out, both bookkeeping entries a hold leaves behind - the hold itself
('New and OnHold') and its release ('New and UnHold') - are dropped, and the status is whatever the
LAST real entry underneath them is. A released order is not assumed to be a plain 'New' again: it
reads as whatever it genuinely was before the freeze (Processed, Ready For Dispatch, or a first-ever
'New' that then relabels to 'Placed'). This only decides the badge; the buyer timeline
(`buyerOrderLogs`) is left exactly as it is.

Callers that also special-case `requested === 'Requested'` must check `isHeldForBuyer` FIRST: an
order can be held while it is Requested, and the hold is what the buyer is meant to see.
*/
export const buyerOrderStatus = (orderData) => {
  if (isHeldForBuyer(orderData)) return HELD_STATUS_LABEL;
  const logs = (orderData && orderData.order_status) || [];
  const visible = logs.filter(
    (log) => log && !isHoldStatus(log.status) && !isUnholdStatus(log.status),
  );
  return visible.length > 0 ? visible[visible.length - 1].status : '';
};

/*
The order's log as the buyer may see it: release entries dropped, hold entries rewritten into the
processed story. Everything else is passed through untouched, and the original array is never
mutated - the same order objects back the seller's and admin's views of these screens elsewhere.

Note that a change request accepted on a held order is deliberately NOT rewritten: the badge goes
back to reading Processed (above), but the timeline keeps the entries it always had.
*/
export const buyerOrderLogs = (orderData) => {
  const logs = (orderData && orderData.order_status) || [];
  return logs
    .filter((log) => log && !isUnholdStatus(log.status))
    .map((log) => (isHoldStatus(log.status)
      ? { ...log, status: HELD_STATUS_LABEL, description: HELD_DESCRIPTION }
      : log));
};

/*
--------------------------------- THE SELLER'S SIDE ---------------------------------------------

The seller is not part of the facade: a hold changes nothing about what they owe on the order, and
they keep seeing every status the order actually carries - the hold entry ("New and OnHold")
included, which is their cue that admin has frozen it for the buyer.

Two adjustments, both about keeping that cue accurate:

  the RELEASE entry     "New and UnHold" is a bookkeeping string, not a status anybody works an
                        order by, and once the hold is off the order is simply the New one it always
                        was - so that is what the seller reads, exactly as admin does (medimny-admin
                        constants/holdForBuyer.js adminLastLogStatus, which this mirrors).

  a still-held 'New'    accepting a change request pushes a plain 'New' entry on top of the hold
                        (see `holdSurvives`). The order is still frozen, so the seller reads it as
                        "New and OnHold" rather than as the New the log literally says - otherwise
                        the one signal they have that admin froze the order disappears the moment
                        the buyer accepts a change.
*/
export const sellerLogStatus = (status) => (isUnholdStatus(status) ? 'New' : status);

// The last entry on an order's log as the SELLER should read it. Also tolerant of an order that
// arrives with no log at all, which the callers' plain array indexing was not.
export const sellerLastLogStatus = (orderData) => {
  const status = lastLogStatus(orderData);
  if (!!(orderData && orderData.holdForBuyer) && status === 'New') return HOLD_FOR_BUYER_STATUS;
  return logStatusLabel(sellerLogStatus(status));
};

/*
The order's log as the SELLER may see it - every entry the order carries, with only a run-on hold
entry upgraded to its spaced spelling. Nothing is dropped or rewritten: the seller is not part of
the buyer's facade, so the hold entry stays visible as the cue that admin froze the order.

The release entry keeps its own label here rather than collapsing to 'New' as the badge does. In a
timeline the entries are a history, and "the hold came off" is a thing that happened; it is only the
order's CURRENT status that is a plain New once the hold is gone.
*/
export const sellerOrderLogs = (orderData) => {
  const logs = (orderData && orderData.order_status) || [];
  return logs.map((log) => (log && logStatusLabel(log.status) !== log.status
    ? { ...log, status: logStatusLabel(log.status) }
    : log));
};

export default isHeldForBuyer;
